import React, { useContext, useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Entete from "./dataset/Entete";
import FooterDashboard from "./dataset/FooterDashboard";
import { ThemeContext } from "./ThemeContext";
import API from "../Authentification/apiAdmin";
import {
    Form,
    FormGroup,
    FormControl,
    FormLabel,
    FormSelect,
    Button,
    Spinner,
} from "react-bootstrap";
import { sous_category_product } from "../Product_Data";
import {
    getProductImages,
    getProductImagePublicIds,
} from "../../Utils/Cloudinary";
// import "../../Styles/AdminDashbord/addProduct.css";

const MAX_IMAGES = 6;

const AddProduct = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { theme } = useContext(ThemeContext);

    const [productData, setProductData] = useState({
        name: "",
        reference: "",
        price: "",
        quantity: "",
        family: "",
        category: "",
        sous_category: "",
        disponibility: "",
        description: "",
    });

    const [existingImages, setExistingImages] = useState([]);
    const [newImages, setNewImages] = useState([]);

    const [loading, setLoading] = useState(false);
    const [trashLoading, setTrashLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const fileInputRef = useRef(null);
    const newImagesRef = useRef([]);

    const token = localStorage.getItem("adminToken");

    const categories = sous_category_product.map((item) => item.category);
    const availableSousCategories =
        sous_category_product.find((item) => item.category === productData.category)
            ?.sous_category || [];

    const handleDisponibilityToggle = (e) => {
        setProductData({
            ...productData,
            disponibility: e.target.checked ? "Oui" : "non",
        });
    };

    useEffect(() => {
        const fetchProduct = async () => {
            if (!id) return;
            setIsEditing(true);
            setLoading(true);
            try {
                const res = await API.get(`/admin/products/${id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setProductData(res.data);

                const publicIds = getProductImagePublicIds(res.data.image);
                const urls = getProductImages(res.data.image);
                setExistingImages(
                    publicIds.map((publicId, i) => ({ publicId, url: urls[i] })),
                );
            } catch (error) {
                toast.error("Erreur lors du chargement du produit");
            } finally {
                setLoading(false);
            }
        };
        fetchProduct();
    }, [id]);

    useEffect(() => {
        newImagesRef.current = newImages;
    }, [newImages]);

    useEffect(() => {
        return () => {
            newImagesRef.current.forEach((img) =>
                URL.revokeObjectURL(img.previewUrl),
            );
        };
    }, []);

    const handleChange = (e) => {
        setProductData({ ...productData, [e.target.name]: e.target.value });
    };

    const handleCategoryChange = (e) => {
        const newCategory = e.target.value;
        const newSubCategories =
            sous_category_product.find((item) => item.category === newCategory)
                ?.sous_category || [];

        setProductData((prev) => ({
            ...prev,
            category: newCategory,
            sous_category: newSubCategories.includes(prev.sous_category)
                ? prev.sous_category
                : "",
        }));
    };

    const totalImages = existingImages.length + newImages.length;

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";

        const remainingSlots = MAX_IMAGES - totalImages;
        if (remainingSlots <= 0) {
            toast.warning(`Maximum ${MAX_IMAGES} images par produit.`);
            return;
        }

        const filesToAdd = files.slice(0, remainingSlots);
        if (files.length > filesToAdd.length) {
            toast.warning(
                `Maximum ${MAX_IMAGES} images par produit — certaines n'ont pas été ajoutées.`,
            );
        }

        const withPreviews = filesToAdd.map((file) => ({
            file,
            previewUrl: URL.createObjectURL(file),
        }));

        setNewImages((prev) => [...prev, ...withPreviews]);
    };

    const removeExistingImage = (publicId) => {
        setExistingImages((prev) =>
            prev.filter((img) => img.publicId !== publicId),
        );
    };

    const removeNewImage = (index) => {
        setNewImages((prev) => {
            URL.revokeObjectURL(prev[index].previewUrl);
            return prev.filter((_, i) => i !== index);
        });
    };

    const handleSubmit = async (e, isDraft = false) => {
        e.preventDefault();
        setLoading(true);

        try {
            const formData = new FormData();
            Object.entries(productData).forEach(([key, value]) => {
                if (key === "image") return;
                formData.append(key, value);
            });

            existingImages.forEach((img) => {
                formData.append("existing_images[]", img.publicId);
            });

            newImages.forEach((img) => {
                formData.append("images[]", img.file);
            });

            formData.append("status", isDraft ? "draft" : "published");

            if (isEditing) {
                formData.append("_method", "PUT");
            }

            const url = isEditing ? `/admin/products/${id}` : "/admin/products";

            await API.post(url, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            toast.success(isEditing ? "Produit modifié " : "Produit ajouté ");
            navigate("/admin/productGrid");
        } catch (error) {
            console.error(error);
            if (error.response?.data?.errors) {
                const messages = Object.values(error.response.data.errors)
                    .flat()
                    .join("\n");
                toast.error(messages);
            } else {
                toast.error("Erreur lors de l’enregistrement ");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="add-product-page">
            <Entete
                title={isEditing ? "Modifier un produit" : "Ajouter un produit"}
            />

            <div
                className="col shadow-sm border border-1 p-3 mt-3"
                style={{ backgroundColor: theme === "dark" ? "black" : "white" }}
            >
                <Form onSubmit={(e) => handleSubmit(e, false)}>
                    <FormGroup className="mb-3">
                        <FormLabel className="label_register">
                            Images du produit ({totalImages}/{MAX_IMAGES})
                        </FormLabel>

                        <div className="nm-gallery-grid">
                            {existingImages.map((img) => (
                                <div className="nm-gallery-slot" key={img.publicId}>
                                    <img src={img.url} alt={productData.name || "Produit"} />
                                    <button
                                        type="button"
                                        className="nm-gallery-remove"
                                        onClick={() => removeExistingImage(img.publicId)}
                                        aria-label="Retirer cette image"
                                    >
                                        ✕
                                    </button>
                                </div>
                            ))}

                            {newImages.map((img, i) => (
                                <div className="nm-gallery-slot" key={`new-${i}`}>
                                    <img src={img.previewUrl} alt="Nouvelle" />
                                    <span className="nm-gallery-badge">Nouvelle</span>
                                    <button
                                        type="button"
                                        className="nm-gallery-remove"
                                        onClick={() => removeNewImage(i)}
                                        aria-label="Retirer cette image"
                                    >
                                        ✕
                                    </button>
                                </div>
                            ))}

                            {totalImages < MAX_IMAGES && (
                                <div
                                    className="nm-gallery-slot nm-gallery-add"
                                    onClick={() => fileInputRef.current?.click()}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ")
                                            fileInputRef.current?.click();
                                    }}
                                >
                                    <span>+ Ajouter</span>
                                </div>
                            )}

                            <FormControl
                                type="file"
                                multiple
                                accept="image/*"
                                onChange={handleImageChange}
                                ref={fileInputRef}
                                className="d-none"
                            />
                        </div>

                        <p className="texte_brut nm-gallery-hint">
                            La première image de la liste est celle utilisée partout ailleurs
                            sur le site (fiche produit en carte, panier, dashboard). Les
                            nouvelles images ajoutées se placent après les images existantes
                            conservées.
                        </p>
                    </FormGroup>

                    {[
                        ["name", "Nom du produit"],
                        ["reference", "Référence"],
                        ["price", "Prix"],
                        ["quantity", "Quantité"],
                        ["family", "Famille"],
                    ].map(([name, label]) => (
                        <FormGroup key={name} className="mb-3">
                            <FormLabel className="label_register">{label}</FormLabel>
                            <FormControl
                                className="input_register"
                                name={name}
                                value={productData[name] || ""}
                                onChange={handleChange}
                            />
                        </FormGroup>
                    ))}

                    <FormGroup className="mb-3">
                        <FormLabel className="label_register">Catégorie</FormLabel>
                        <FormSelect
                            className="input_register"
                            name="category"
                            value={productData.category || ""}
                            onChange={handleCategoryChange}
                        >
                            <option value="">-- Choisir une catégorie --</option>
                            {categories.map((cat) => (
                                <option key={cat} value={cat}>
                                    {cat}
                                </option>
                            ))}
                        </FormSelect>
                    </FormGroup>

                    <FormGroup className="mb-3">
                        <FormLabel className="label_register">Sous-catégorie</FormLabel>
                        <FormSelect
                            className="input_register"
                            name="sous_category"
                            value={productData.sous_category || ""}
                            onChange={handleChange}
                            disabled={!productData.category}
                        >
                            <option value="">
                                {productData.category
                                    ? "-- Choisir une sous-catégorie --"
                                    : "-- Choisir d'abord une catégorie --"}
                            </option>
                            {availableSousCategories.map((sc) => (
                                <option key={sc} value={sc}>
                                    {sc}
                                </option>
                            ))}
                        </FormSelect>
                    </FormGroup>

                    <FormGroup className="mb-3 d-flex align-items-center gap-2">
                        <FormLabel className="label_register mb-0">Disponibilité</FormLabel>
                        <Form.Check
                            type="switch"
                            id="disponibility-switch"
                            checked={productData.disponibility === "Oui"}
                            onChange={handleDisponibilityToggle}
                            label={
                                productData.disponibility === "Oui"
                                    ? "Disponible"
                                    : "Indisponible"
                            }
                        />
                    </FormGroup>

                    <FormGroup className="mb-3">
                        <FormLabel className="label_register">Description</FormLabel>
                        <FormControl
                            as="textarea"
                            rows={4}
                            name="description"
                            value={productData.description || ""}
                            onChange={handleChange}
                        />
                    </FormGroup>

                    <div className="d-flex flex-column flex-sm-row gap-2">
                        <Button variant="secondary" disabled={loading}>
                            {trashLoading ? (
                                <Spinner animation="border" size="sm" />
                            ) : (
                                "Brouillon"
                            )}
                        </Button>
                        <Button type="submit" variant="primary" disabled={loading}>
                            {loading ? (
                                <Spinner animation="border" size="sm" />
                            ) : (
                                "Enregistrer"
                            )}
                        </Button>
                    </div>
                </Form>
            </div>

            <FooterDashboard />
        </div>
    );
};

export default AddProduct;